import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Typography, Tooltip } from '@mui/material';
import { NotificationsActive as NotificationsActiveIcon } from '@mui/icons-material';
import NotificationBell from './NotificationBell';
import { notificationApi } from 'src/utils/notificationApi';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import { useNavigate } from 'react-router-dom';
import pushNotifications from 'src/utils/pushNotifications';
import { useAuth } from 'src/utils/Authorization/AuthContext';
import UserInfo from 'src/utils/Authorization/UserInfo';

/**
 * NotificationCenter Component
 * Main component that manages notifications and handles user interactions
 */
function NotificationCenter() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const userInfo = UserInfo();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [permission, setPermission] = useState('default');

  // Get current user ID
  const getUserId = () => {
    return authUser?.pk || userInfo?.pk || null;
  };

  // Check push notification status - check per user
  useEffect(() => {
    const checkPushStatus = async () => {
      if (pushNotifications.isPushNotificationSupported()) {
        const currentPermission = pushNotifications.getNotificationPermission();
        setPermission(currentPermission);

        // Check if there's an existing subscription
        const subscription = await pushNotifications.getExistingSubscription();
        
        // Even if browser permission is granted, check if this user has been asked
        // This handles the case where User A granted permission, then User B logs in
        const userId = getUserId();
        
        if (userId) {
          const userGrantedKey = `notification_permission_granted_${userId}`;
          const userGranted = localStorage.getItem(userGrantedKey);
          
          // Only enable if permission is granted AND user has been asked and granted
          setPushEnabled(currentPermission === 'granted' && !!subscription && userGranted === 'true');
        } else {
          setPushEnabled(currentPermission === 'granted' && !!subscription);
        }
      }
    };

    checkPushStatus();
  }, [authUser, userInfo]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationApi.getNotifications({
        limit: 10,
        ordering: '-created_at'
      });

      const notificationList = response?.results || response?.data || response || [];
      setNotifications(notificationList);

      // Count unread
      const unread = notificationList.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Don't show error toast for initial load
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationApi.getUnreadCount();
      setUnreadCount(response?.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );

      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      showErrorToast('Failed to mark notification as read');
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );

      // Reset unread count
      setUnreadCount(0);

      showToast('All notifications marked as read', 'success');
    } catch (error) {
      console.error('Error marking all as read:', error);
      showErrorToast('Failed to mark all as read');
    }
  };

  // Delete all notifications
  const handleDeleteAll = async () => {
    try {
      await notificationApi.deleteAllNotifications();

      // Update local state
      setNotifications([]);
      setUnreadCount(0);

      showToast('All notifications deleted', 'success');
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      showErrorToast('Failed to delete all notifications');
    }
  };

  // Delete a specific notification
  const handleDeleteNotification = async (notificationId) => {
    try {
      await notificationApi.deleteNotification(notificationId);

      // Update local state
      const deletedNotification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Update unread count if the notification was unread
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      showToast('Notification deleted', 'success');
    } catch (error) {
      console.error('Error deleting notification:', error);
      showErrorToast('Failed to delete notification');
    }
  };


  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if not already
      if (!notification.is_read) {
        await handleMarkAsRead(notification.id);
      }

      // Navigate to the action URL if available
      if (notification.action_url) {
        navigate(notification.action_url);
      } else if (notification.data) {
        // Handle navigation based on notification type and data
        const { type, data } = notification;

        switch (type) {
          case 'TASK_ASSIGNED':
            if (data.company_id) {
              navigate(`/companies/${data.company_id}?tab=tasks`);
            }
            break;

          case 'STAGE_CHANGED':
          case 'COMPANY_ASSIGNED':
            if (data.company_id) {
              navigate(`/companies/${data.company_id}`);
            }
            break;

          case 'DOCUMENT_UPLOADED':
            if (data.company_id) {
              navigate(`/companies/${data.company_id}?tab=documents`);
            }
            break;

          case 'MEETING_SCHEDULED':
            if (data.company_id) {
              navigate(`/companies/${data.company_id}?tab=meetings`);
            }
            break;

          default:
            // Default to dashboard or home
            navigate('/dashboard');
            break;
        }
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      showErrorToast('Failed to navigate to notification');
    }
  };

  // Handle enable push notifications
  const handleEnablePush = async () => {
    try {
      // Check current permission first
      let currentPermission = pushNotifications.getNotificationPermission();
      
      // If permission is default, request it
      if (currentPermission === 'default') {
        currentPermission = await pushNotifications.requestNotificationPermission();
        setPermission(currentPermission);
      }

      if (currentPermission === 'denied') {
        showErrorToast('Notification permission denied. Please enable in browser settings.');
        return;
      }

      if (currentPermission === 'granted') {
        // Register service worker
        await pushNotifications.registerServiceWorker();

        // Subscribe to push
        const subscription = await pushNotifications.subscribeToPushNotifications();

        // Send to backend
        const subscriptionData = pushNotifications.getSubscriptionJSON(subscription);
        const deviceType = pushNotifications.getBrowserType();

        await notificationApi.registerDevice({
          ...subscriptionData,
          device_type: deviceType
        });

        // Store user-specific permission state
        const userId = getUserId();
        if (userId) {
          localStorage.setItem(`notification_permission_granted_${userId}`, 'true');
        }

        setPushEnabled(true);
        showToast('Push notifications enabled!', 'success');
      }
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      showErrorToast('Failed to enable push notifications');
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Set up polling for new notifications (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Listen for push notifications while app is open
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        // Refresh notifications when a new push is received
        if (event.data && event.data.type === 'push-notification') {
          fetchNotifications();
        }
      });
    }
  }, []);

  // If notifications not enabled, show enable button
  if (!pushEnabled && permission !== 'granted') {
    return (
      <Tooltip title="Enable push notifications to receive updates in real-time" placement="right" arrow>
        <Box
          data-notification-bell="true"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer'
          }}
          onClick={handleEnablePush}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              border: '2px dashed',
              borderColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.lighter',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: 'primary.light',
                borderColor: 'primary.dark'
              }
            }}
          >
            <NotificationsActiveIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          </Box>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', textAlign: 'center' }}>
            Enable
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <NotificationBell
      unreadCount={unreadCount}
      notifications={notifications}
      onMarkAsRead={handleMarkAsRead}
      onMarkAllAsRead={handleMarkAllAsRead}
      onNotificationClick={handleNotificationClick}
      onRefresh={fetchNotifications}
      onDeleteAll={handleDeleteAll}
      onDeleteNotification={handleDeleteNotification}
      onOpen={fetchNotifications}
      loading={loading}
    />
  );
}

export default NotificationCenter;
